import GuideLink from 'components/GuideLink'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useAccount } from 'wagmi'
import MoonScene, { nightSky, MoonIcon } from '../TokenPages/art/MoonScene'
import { Icon } from '../TokenPages/icons'
import { Fluent } from '../TradingCompetitionV3/ui'
import { CREATE_PATH } from './config'
import { fmtDay, fmtSpan, fmtUsd } from './format'
import { useLockPage } from './hooks'
import { DEFAULT_QUERY, queryFromParams, queryToParams } from './logic/query'
import type { KindFilter, LockQuery, SortKey, StatusFilter } from './types'
import ContractsPanel from './components/ContractsPanel'
import FreeMark, { useLockPrice } from './components/FreeMark'
import LockRow from './components/LockRow'
import Seal from './components/Seal'
import { CtaLink, DISPLAY, FigLabel, FigValue, GhostCta, MONO, Page, Panel, Segmented, SegBtn, Skel, Stack, useNow } from './ui'

/*
 * The lock landing (redesign, Bez 2026-10-09: "too cluttered, no feeling of a lock and of safety, FREE must be bold").
 * Copy order: what it does for you (sealed until your date) -> FREE -> who cannot take it out -> one action -> the live readout.
 * Then three things the contract enforces, then the locks themselves with only search and status in view, and the two contracts
 * behind one disclosure. Every protective sentence is proved from the verified sources (see DESIGN.md "Claims and their proof").
 */

const Hero = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid rgba(127, 212, 232, 0.28);
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.14), 0 24px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(5, 7, 19, 0.6);
  ${nightSky}
  padding: 20px 16px 16px;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: 'vault' 'text' 'readout';
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 32px 32px 24px;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    grid-template-areas: 'text vault' 'readout readout';
    column-gap: 32px;
    row-gap: 28px;
    padding: 44px 48px 28px;
    align-items: center;
  }
`
const Sky = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  opacity: 0.7;
  mask-image: linear-gradient(180deg, #000 0%, transparent 75%);
  -webkit-mask-image: linear-gradient(180deg, #000 0%, transparent 75%);
`
/** a cyan floor light under the padlock: the one place the hero glows */
const LockCol = styled.div`
  grid-area: vault;
  position: relative;
  justify-self: center;
  width: min(176px, 48vw);
  &::before {
    content: '';
    position: absolute;
    inset: -18% -30%;
    z-index: -1;
    background: radial-gradient(closest-side, rgba(127, 212, 232, 0.22), rgba(126, 0, 254, 0.12) 55%, transparent 100%);
  }
  ${({ theme }) => theme.mediaQueries.md} {
    width: 260px;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    width: min(340px, 100%);
  }
`
const HeroText = styled.div`
  grid-area: text;
  display: grid;
  gap: 14px;
  min-width: 0;
  ${({ theme }) => theme.mediaQueries.lg} {
    gap: 20px;
  }
`
const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  .eb {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-family: ${MONO};
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #7fd4e8;
    white-space: nowrap;
  }
  @media (max-width: 479px) {
    .chain {
      display: none;
    }
  }
`
const H1 = styled.h1`
  margin: 0;
  font-family: ${DISPLAY};
  font-weight: 800;
  font-size: 32px;
  line-height: 36px;
  letter-spacing: -0.02em;
  color: #f4f6ff;
  text-wrap: balance;
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 46px;
    line-height: 50px;
  }
  ${({ theme }) => theme.mediaQueries.xl} {
    font-size: 54px;
    line-height: 58px;
  }
`
const Lede = styled.p`
  margin: 0;
  max-width: 540px;
  font-size: 16px;
  line-height: 24px;
  color: #c9d2e6;
  b {
    color: #f4f6ff;
    font-weight: 700;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 18px;
    line-height: 28px;
  }
`
const Cta = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  max-width: 560px;
  a,
  button {
    min-height: 56px;
    font-size: 17px;
  }
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  }
`
/** the live readout under the hero: three equal cells, the vault's instrument panel */
const Readout = styled.div`
  grid-area: readout;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-radius: 18px;
  background: rgba(5, 7, 19, 0.62);
  border: 1px solid rgba(180, 200, 255, 0.16);
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.06);
  > div {
    display: grid;
    gap: 4px;
    min-width: 0;
    padding: 12px 10px;
  }
  > div + div {
    border-left: 1px solid rgba(180, 200, 255, 0.14);
  }
  ${({ theme }) => theme.mediaQueries.md} {
    > div {
      padding: 14px 20px;
    }
  }
`

const Promises = styled.section`
  display: grid;
  gap: 14px;
`
const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  h2 {
    margin: 0;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.01em;
    color: #f4f6ff;
  }
  p {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: #c9d2e6;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    h2 {
      font-size: 28px;
      line-height: 34px;
    }
  }
`
const Tiles = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
`
const Tile = styled.div`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  padding: 16px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(127, 212, 232, 0.08) 0%, rgba(5, 7, 19, 0) 60%), #0a0e22;
  border: 1px solid rgba(180, 200, 255, 0.16);
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.1), 0 10px 30px rgba(0, 0, 0, 0.35);
  align-content: start;
  .icon {
    display: grid;
    place-items: center;
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: radial-gradient(circle at 50% 35%, rgba(127, 212, 232, 0.22), rgba(5, 7, 19, 0.7) 75%);
    box-shadow: inset 0 0 0 1px rgba(127, 212, 232, 0.3);
  }
  h3 {
    margin: 0 0 4px;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 18px;
    line-height: 24px;
    color: #f4f6ff;
  }
  p {
    margin: 0;
    font-size: 15px;
    line-height: 22px;
    color: #c9d2e6;
  }
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: minmax(0, 1fr);
    padding: 20px;
  }
`

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 8px 0 16px;
  border-radius: 16px;
  background: rgba(5, 7, 19, 0.7);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.05);
  color: ${({ theme }) => theme.colors.textSubtle};
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  input {
    flex: 1;
    min-width: 0;
    height: 50px;
    border: 0;
    outline: 0;
    background: transparent;
    font: inherit;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.text};
    &::placeholder {
      color: ${({ theme }) => theme.colors.textSubtle};
      opacity: 0.85;
    }
  }
`
const ClearBtn = styled.button`
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSubtle};
  background: transparent;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
  }
`
/** status chips and the Filters toggle on one line; on a phone the chips scroll sideways inside their own row */
const FilterRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
`
const Chips = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 2px;
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    flex-wrap: wrap;
    overflow: visible;
  }
`
const StatusChip = styled.button<{ $on?: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme, $on }) => ($on ? '#050713' : theme.colors.text)};
  background: ${({ $on }) => ($on ? '#7fd4e8' : 'transparent')};
  border: 1px solid ${({ theme, $on }) => ($on ? '#7fd4e8' : theme.colors.cardBorder)};
  &:hover {
    border-color: #7fd4e8;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  small {
    font-family: ${MONO};
    font-size: 12px;
    font-weight: 500;
    opacity: 0.8;
  }
`
const FiltersBtn = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  min-width: 118px;
  padding: 0 14px;
  border-radius: 14px;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $on }) => ($on ? '#f4f6ff' : '#c9d2e6')};
  background: ${({ $on }) => ($on ? 'rgba(127, 212, 232, 0.14)' : 'rgba(5, 7, 19, 0.7)')};
  border: 1px solid ${({ $on }) => ($on ? 'rgba(127, 212, 232, 0.55)' : 'rgba(180, 200, 255, 0.2)')};
  &:hover {
    border-color: #7fd4e8;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  svg {
    transition: transform 0.15s ease;
    transform: rotate(${({ $on }) => ($on ? 180 : 0)}deg);
  }
  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`
const MoreFilters = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: minmax(0, 1fr) 240px;
    align-items: center;
  }
`
const KindSeg = styled(Segmented)`
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`
const SortSelect = styled.select`
  min-height: 44px;
  width: 100%;
  padding: 0 12px;
  border-radius: 14px;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  background: rgba(5, 7, 19, 0.7);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
  }
`
const List = styled.div`
  display: grid;
  gap: 10px;
`
const ListHead = styled.div`
  display: none;
  ${({ theme }) => theme.mediaQueries.lg} {
    display: grid;
    grid-template-columns: 44px minmax(0, 2.3fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.25fr) 150px 20px;
    gap: 14px;
    padding: 0 18px 0 14px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`
const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`
const PagerBtn = styled.button`
  min-height: 44px;
  padding: 0 18px;
  border-radius: 14px;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
  &:not(:disabled):hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`
const Empty = styled.div`
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 28px 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSubtle};
  b {
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 20px;
    color: ${({ theme }) => theme.colors.text};
  }
`
/** the two contracts, one tap away: a full-width row that says what is inside before it is opened */
const Disclosure = styled.button<{ $open: boolean }>`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 64px;
  padding: 10px 16px 10px 10px;
  border-radius: 18px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: #f4f6ff;
  background: #0a0e22;
  border: 1px solid ${({ $open }) => ($open ? 'rgba(127, 212, 232, 0.5)' : 'rgba(180, 200, 255, 0.18)')};
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.08);
  &:hover {
    border-color: rgba(127, 212, 232, 0.7);
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 3px;
  }
  .ic {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    color: #7fd4e8;
    background: rgba(127, 212, 232, 0.1);
  }
  b {
    display: block;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 17px;
    line-height: 22px;
  }
  small {
    display: block;
    font-size: 13px;
    line-height: 18px;
    color: #c9d2e6;
  }
  > svg {
    color: #c9d2e6;
    transition: transform 0.15s ease;
    transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
  }
  @media (prefers-reduced-motion: reduce) {
    > svg {
      transition: none;
    }
  }
`

const KINDS: Array<{ key: KindFilter; label: string }> = [
  { key: 'all', label: 'All types' },
  { key: 'v3-position', label: 'V3 positions' },
  { key: 'lp-v2', label: 'V2 LP' },
  { key: 'token', label: 'Tokens' },
]
const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: 'value', label: 'Most value locked' },
  { key: 'unlock', label: 'Unlocks soonest' },
  { key: 'newest', label: 'Newest' },
]
/** Three things the two lock contracts enforce, each proved from the verified sources (DESIGN.md "Claims and their proof"). */
const PROMISES: Array<{ icon: string; title: string; body: string; testid: string }> = [
  { icon: 'shield', title: 'Nobody takes it out early', body: 'Not the owner, not CADINU. The contract pays a lock out only to its owner, and only once its date has come.', testid: 'promise-early' },
  { icon: 'hourglass', title: 'Later, never sooner', body: 'The owner can extend the unlock date. No function in either contract moves it earlier.', testid: 'promise-extend' },
  { icon: 'magnifier', title: 'Check any lock yourself', body: 'Every lock gets a certificate read straight from the verified contract, with its countdown and a QR to verify.', testid: 'promise-check' },
]

export default function Explorer() {
  const router = useRouter()
  const { address } = useAccount()
  const now = useNow()
  const price = useLockPrice()
  const query = useMemo<LockQuery>(() => (router.isReady ? queryFromParams(router.query) : DEFAULT_QUERY), [router.isReady, router.query])
  const { data, error, isLoading } = useLockPage(router.isReady ? query : null)
  const [text, setText] = useState('')
  const [moreOpen, setMoreOpen] = useState(false)
  const [contractsOpen, setContractsOpen] = useState(false)
  const typed = useRef(false)

  // the box shows what the url holds (a shared link, back and forward), but never overwrites what is being typed
  useEffect(() => {
    if (router.isReady && !typed.current) setText(query.q)
  }, [router.isReady, query.q])

  const go = (patch: Partial<LockQuery>) => {
    const next: LockQuery = { ...query, page: 1, ...patch }
    router.replace({ pathname: router.pathname, query: queryToParams(next) }, undefined, { shallow: true, scroll: false })
  }
  useEffect(() => {
    if (!router.isReady || text === query.q) return undefined
    const t = setTimeout(() => {
      typed.current = false
      go({ q: text })
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text])

  const totals = data?.totals
  const counts: Record<StatusFilter, number | undefined> = { active: totals?.active, soon: totals?.soon, due: totals?.due, released: totals?.released, all: totals?.total }
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1
  const mine = address && query.owner === address.toLowerCase()
  const filtered = query.q !== '' || query.kind !== 'all' || query.status !== 'active' || !!query.owner || !!query.token
  // a type or a sort other than the default keeps the extra filters in view (a shared link shows what it filters by)
  const extraOn = query.kind !== DEFAULT_QUERY.kind || query.sort !== DEFAULT_QUERY.sort
  useEffect(() => {
    if (router.isReady && extraOn) setMoreOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])
  const showMore = moreOpen
  const free = price.kind === 'free'

  return (
    <Page data-testid="lock-explorer">
      <NextSeo title="Lock" description="Lock liquidity and tokens on BNB Chain until a date you choose. Locking is free: you pay only the network fee. Nobody, CADINU included, can withdraw a lock before its date." />
      <Stack $gap={28}>
        <Hero data-testid="lock-hero">
          <Sky aria-hidden="true">
            <MoonScene scene="hero" moon={false} wolf={false} still align="xMidYMin" />
          </Sky>
          <LockCol>
            <Seal progress={1} tone="locked" animate decorative testid="hero-lock" />
          </LockCol>
          <HeroText>
            <Eyebrow>
              <span className="eb">
                <Icon name="lock" size={14} strokeWidth={2.4} />
                <span>
                  CADINU Lock<span className="chain"> · BNB Chain</span>
                </span>
              </span>
              <GuideLink guide="lock" />
            </Eyebrow>
            <H1>Sealed until the date you choose.</H1>
            <FreeMark price={price} testid="free-mark" />
            <Lede>
              Liquidity and tokens go into a verified contract. <b>Nobody, CADINU included, can take them out before the unlock date.</b>
            </Lede>
            <Cta>
              <CtaLink href={CREATE_PATH} variant="primary" data-testid="lock-something">
                <Icon name="lock" size={20} strokeWidth={2.4} />{free ? 'Lock now, free' : 'Lock now'}
              </CtaLink>
              {address ? (
                <GhostCta as="button" type="button" onClick={() => go({ owner: mine ? undefined : address.toLowerCase(), status: 'all' })} aria-pressed={!!mine} data-testid="my-locks" style={{ background: 'rgba(5, 7, 19, 0.5)' }}>
                  {mine ? 'Show all locks' : 'My locks'}
                </GhostCta>
              ) : (
                <GhostCta href="#locks" data-testid="browse-locks" style={{ background: 'rgba(5, 7, 19, 0.5)' }}>
                  See the locks
                </GhostCta>
              )}
            </Cta>
          </HeroText>
          <Readout data-testid="totals">
            <div>
              <FigLabel>Locked now</FigLabel>
              <FigValue $size={22} data-testid="total-locked">{totals ? fmtUsd(totals.lockedUsd) : <Skel $h={24} $w="70%" />}</FigValue>
            </div>
            <div>
              <FigLabel>Active locks</FigLabel>
              <FigValue $size={22} data-testid="total-active">{totals ? totals.active : <Skel $h={24} $w="40%" />}</FigValue>
            </div>
            <div>
              <FigLabel>Next unlock</FigLabel>
              <FigValue $size={22} data-testid="total-next">{totals ? (totals.nextUnlockAt && now !== null ? fmtSpan(totals.nextUnlockAt - now) : totals.nextUnlockAt ? fmtDay(totals.nextUnlockAt) : '—') : <Skel $h={24} $w="60%" />}</FigValue>
            </div>
          </Readout>
        </Hero>

        <Promises aria-labelledby="promises-title" data-testid="promises">
          <SectionHead>
            <h2 id="promises-title">What the contract enforces</h2>
            <p>Read from the verified source of both lock contracts.</p>
          </SectionHead>
          <Tiles>
            {PROMISES.map((p) => (
              <Tile key={p.testid} data-testid={p.testid}>
                <span className="icon">
                  <Fluent name={p.icon} size={40} />
                </span>
                <div>
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                </div>
              </Tile>
            ))}
          </Tiles>
        </Promises>

        <section id="locks" aria-labelledby="locks-title" style={{ display: 'grid', gap: 12, scrollMarginTop: 80 }}>
          <SectionHead>
            <h2 id="locks-title">All locks</h2>
            {data ? <p data-testid="locks-count">{data.total} {data.total === 1 ? 'lock' : 'locks'} {query.status === 'all' ? 'in total' : 'shown'}</p> : null}
          </SectionHead>
          <SearchBox>
            <Icon name="search" size={20} />
            <input
              type="search"
              inputMode="search"
              value={text}
              onChange={(e) => {
                typed.current = true
                setText(e.target.value)
              }}
              placeholder="Search a token, a pair, an owner or a lock number"
              aria-label="Search locks"
              data-testid="lock-search"
              autoComplete="off"
              spellCheck={false}
            />
            {text ? (
              <ClearBtn type="button" aria-label="Clear the search" onClick={() => { typed.current = false; setText(''); go({ q: '' }) }}>
                <Icon name="close" size={18} />
              </ClearBtn>
            ) : null}
          </SearchBox>
          <FilterRow>
            <Chips data-testid="status-filter" role="group" aria-label="Status">
              {(
                [
                  ['active', 'Active'],
                  ['soon', 'Unlocking soon'],
                  ['due', 'Ready to unlock'],
                  ['released', 'Unlocked'],
                  ['all', 'All'],
                ] as Array<[StatusFilter, string]>
              ).map(([k, label]) => (
                <StatusChip key={k} type="button" $on={query.status === k} aria-pressed={query.status === k} onClick={() => go({ status: k })} data-testid={`status-${k}`}>
                  {label}
                  {counts[k] !== undefined ? <small>{counts[k]}</small> : null}
                </StatusChip>
              ))}
            </Chips>
            <FiltersBtn type="button" $on={showMore} aria-expanded={showMore} aria-controls="more-filters" onClick={() => setMoreOpen(!moreOpen)} data-testid="more-filters">
              Filters{extraOn ? <small style={{ fontFamily: MONO, fontSize: 12, color: '#7fd4e8' }}>on</small> : null} <Icon name="chevronDown" size={16} strokeWidth={2.4} />
            </FiltersBtn>
          </FilterRow>
          {showMore ? (
            <MoreFilters id="more-filters" data-testid="more-filters-panel">
              <KindSeg $cols={4} data-testid="kind-filter" role="tablist" aria-label="Type of lock">
                {KINDS.map((k) => (
                  <SegBtn key={k.key} type="button" role="tab" aria-selected={query.kind === k.key} $on={query.kind === k.key} onClick={() => go({ kind: k.key })} data-testid={`kind-${k.key}`}>
                    {k.label}
                  </SegBtn>
                ))}
              </KindSeg>
              <SortSelect value={query.sort} onChange={(e) => go({ sort: e.target.value as SortKey })} aria-label="Sort locks" data-testid="sort">
                {SORTS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </SortSelect>
            </MoreFilters>
          ) : null}

          {query.owner || query.token ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, color: '#c9d2e6', fontSize: 14 }} data-testid="scope-note">
              {query.owner ? <>Locks owned by <span style={{ fontFamily: MONO }}>{query.owner.slice(0, 6)}…{query.owner.slice(-4)}</span></> : <>Locks of <span style={{ fontFamily: MONO }}>{query.token!.slice(0, 6)}…{query.token!.slice(-4)}</span></>}
              <PagerBtn type="button" onClick={() => go({ owner: undefined, token: undefined })}>Show all</PagerBtn>
            </div>
          ) : null}

          <div aria-label="Locks" data-testid="lock-list" aria-busy={isLoading}>
            <List>
              <ListHead aria-hidden="true">
                <span />
                <span>Lock</span>
                <span>Value</span>
                <span>Share</span>
                <span>Unlocks</span>
                <span>Status</span>
                <span />
              </ListHead>
              {isLoading && !data ? Array.from({ length: 3 }, (_, i) => <Skel key={i} $h={84} $r={18} />) : null}
              {error && !data ? (
                <Panel data-testid="list-error">
                  <Empty>
                    <MoonIcon size={96} />
                    <b>The chain did not answer</b>
                    <span>Locks are read straight from the contracts. It is retried automatically.</span>
                  </Empty>
                </Panel>
              ) : null}
              {data?.items.map((l) => <LockRow key={`${l.type}-${l.id}`} lock={l} now={now} readAt={data.readAt} />)}
              {data && data.items.length === 0 ? (
                <Panel data-testid="list-empty">
                  <Empty>
                    <MoonIcon size={96} />
                    <b>{filtered ? 'No lock matches' : 'Nothing is locked yet'}</b>
                    <span>{filtered ? 'Try another word, or show every status.' : 'Be the first: lock liquidity or tokens and get a certificate.'}</span>
                    {filtered ? (
                      <PagerBtn type="button" onClick={() => { typed.current = false; setText(''); router.replace({ pathname: router.pathname, query: {} }, undefined, { shallow: true, scroll: false }) }}>
                        Clear filters
                      </PagerBtn>
                    ) : (
                      <CtaLink href={CREATE_PATH} variant="primary">{free ? 'Lock now, free' : 'Lock now'}</CtaLink>
                    )}
                  </Empty>
                </Panel>
              ) : null}
            </List>
            {data && pages > 1 ? (
              <Pager style={{ marginTop: 14 }}>
                <PagerBtn type="button" disabled={data.page <= 1} onClick={() => go({ page: data.page - 1 })}>Previous</PagerBtn>
                <span style={{ fontFamily: MONO, fontSize: 14, color: '#c9d2e6' }}>Page {data.page} of {pages}</span>
                <PagerBtn type="button" disabled={data.page >= pages} onClick={() => go({ page: data.page + 1 })}>Next</PagerBtn>
              </Pager>
            ) : null}
            {data ? (
              <p style={{ margin: '14px 0 0', fontSize: 13, lineHeight: '18px', color: '#c9d2e6' }} data-testid="read-note">
                Read from the chain {now !== null ? `${fmtSpan(Math.max(0, now - data.readAt))} ago` : 'just now'}
                {totals && totals.unpriced > 0 ? `; ${totals.unpriced} could not be priced and show no dollar value.` : '.'}
              </p>
            ) : null}
          </div>
        </section>

        <div style={{ display: 'grid', gap: 12 }}>
          <Disclosure type="button" $open={contractsOpen} aria-expanded={contractsOpen} aria-controls="lock-contracts" onClick={() => setContractsOpen(!contractsOpen)} data-testid="contracts-toggle">
            <span className="ic">
              <Icon name="shieldCheck" size={22} />
            </span>
            <span>
              <b>The two lock contracts</b>
              <small>Addresses, verified source and the live lock fee</small>
            </span>
            <Icon name="chevronDown" size={20} strokeWidth={2.4} />
          </Disclosure>
          {/* kept in the page while closed, so the fee lines stay readable by tests and by search engines */}
          <div id="lock-contracts" hidden={!contractsOpen}>
            <ContractsPanel />
          </div>
        </div>
      </Stack>
    </Page>
  )
}
